import {View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {atoms as a} from '#/alf'
import {Text} from '#/components/Typography'

/**
 * The one thing a card in a work grid says that no other card of its kind does.
 *
 * A grid of work is a grid of the app's own cards, drawn by the app's own
 * components (`CapsulePostCard`, `FlashBookFeedEmbed`), which is what makes a
 * capsule in a classroom look like the same capsule at the front. What those cards
 * cannot say is where the work lives or whether it has been finished: a capsule
 * written for a room is deliberately absent from every profile and feed, and a
 * draft has never been anywhere at all. For a reader, both are the difference
 * between "it is not there" and "this is broken", so the card carries it as a
 * badge.
 *
 * Drawn over the card rather than under it because a grid cell's caption belongs
 * to the work — title, author, level — and a badge that took a line of its own
 * would push those words into a second row for a fact about the work's state.
 *
 * Two words wear this pill — `Classroom only` and `Draft` — and the pill knows
 * neither of them: the label is the caller's. What the component owns is the
 * corner and the ink, so that two facts about work that are otherwise unrelated
 * still read as the same kind of note.
 */
export function WorkCardBadge({
  label,
  belowKindChip = false,
}: {
  label: string
  /**
   * Set for a card whose own top-left corner is already taken (a book's cover).
   *
   * `FlashBookFeedEmbed` prints the book's own kind there — "Storybook", "Video" —
   * in the same 8px inset, so a badge on it lands on top of that word rather than
   * beside it. Dropping to 40px clears the chip's row and lands on the cover's
   * empty art, which is the one place a book's cover keeps clear.
   */
  belowKindChip?: boolean
}) {
  return (
    <View
      style={[a.absolute, a.z_10, {top: belowKindChip ? 40 : 8, left: 8}]}
      pointerEvents="none">
      <View
        style={[
          a.rounded_full,
          a.px_sm,
          {paddingVertical: 3, backgroundColor: 'rgba(0,0,0,0.55)'},
        ]}>
        <Text
          style={[
            a.text_xs,
            a.font_semi_bold,
            {color: 'white', lineHeight: 14},
          ]}>
          {label}
        </Text>
      </View>
    </View>
  )
}

/**
 * The badge for work the creator has not published yet.
 *
 * A draft is the opposite of the room's own work: it is private in the sense that
 * matters — nobody else has been given it, and the appview decides who may list a
 * creator's drafts (see `#/lib/flashbookDrafts`) — but it is *unfinished*, and the
 * card owed to it is a badge that says so rather than a card that pretends it is
 * published.
 */
export function DraftBadge() {
  const {t: l} = useLingui()
  return <WorkCardBadge label={l`Draft`} />
}
